import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('accessToken');
      localStorage.removeItem('currentUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  loginAsUser: (userId) => api.post('/auth/login-as-user', { user_id: userId }),
  me: () => api.get('/auth/me'),
};

// Clients API
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  setFavorite: (id, favorite) => api.put(`/clients/${id}/favorite`, { favorite }),
  merge: (data) => api.post('/clients/merge', data),
  // Trash/Hopper methods
  listTrash: (params) => api.get('/clients/trash/list', { params }),
  restore: (id) => api.post(`/clients/trash/${id}/restore`),
  purge: (id) => api.delete(`/clients/trash/${id}/purge`),
  purgeAll: () => api.post('/clients/trash/purge-all'),
  bulkPurge: (clientIds) => api.post('/clients/trash/bulk-purge', clientIds),
  // Notes
  listNotes: (clientId) => api.get(`/clients/${clientId}/notes`),
  addNote: (clientId, text) => api.post(`/clients/${clientId}/notes`, { text }),
  updateNote: (clientId, noteId, text) => api.put(`/clients/${clientId}/notes/${noteId}`, { text }),
  deleteNote: (clientId, noteId) => api.delete(`/clients/${clientId}/notes/${noteId}`),
  // Phone balance
  addFunds: (clientId, amount, note) => api.post(`/clients/${clientId}/add-funds`, { amount, note }),
  getMyPhoneBalance: () => api.get('/clients/me/phone-balance'),
  // Account credit
  addCredit: (clientId, amount, description) => api.post(`/clients/${clientId}/credits`, { amount, description }),
  getCredits: (clientId) => api.get(`/clients/${clientId}/credits`),
  getMyCredits: () => api.get('/clients/me/credits'),
  emailProductsSummary: (clientId) => api.post(`/clients/${clientId}/email-products-summary`),
  clientAddFunds: (amount, originUrl) => api.post('/clients/me/add-funds', { amount, origin_url: originUrl }),
};

// Services API
export const servicesAPI = {
  list: (params) => api.get('/services', { params }),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  suspend: (id) => api.post(`/services/${id}/suspend`),
  unsuspend: (id) => api.post(`/services/${id}/unsuspend`),
};

// Products API
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

export const resellerMailAPI = {
  listDepartments: () => api.get('/reseller-mail/departments'),
  pricing: (productId) => api.get(`/reseller-mail/pricing/${productId}`),
  listAccounts: (params) => api.get('/reseller-mail/accounts', { params }),
  addAccount: (data) => api.post('/reseller-mail/accounts', data),
  updateAccount: (id, data) => api.put(`/reseller-mail/accounts/${id}`, data),
  addMailboxes: (id, mailboxes) => api.post(`/reseller-mail/accounts/${id}/mailboxes`, { mailboxes }),
  setPriceOverride: (id, data) => api.put(`/reseller-mail/accounts/${id}/price-override`, data),
  listActivity: (params) => api.get('/reseller-mail/activity', { params }),
  // admin-only
  convertToDirect: (id, data) => api.post(`/reseller-mail/accounts/${id}/convert-to-direct`, data),
};

export const a2gMailAPI = {
  getSyncStatus: () => api.get('/a2g-mail/reseller-sync-status'),
  provisionReseller: (clientId) => api.post(`/a2g-mail/provision-reseller/${clientId}`),
};

// Invoices API
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  totals: () => api.get('/invoices/totals'),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  markPaid: (id) => api.post(`/invoices/${id}/mark-paid`),
  updateStatus: (id, status) => api.post(`/invoices/${id}/status?status=${status}`),
  refund: (id, amount = null, reason = 'requested_by_customer') => {
    const params = new URLSearchParams();
    if (amount !== null) params.append('amount', amount);
    if (reason) params.append('reason', reason);
    const queryString = params.toString();
    return api.post(`/invoices/${id}/refund${queryString ? '?' + queryString : ''}`);
  },
  pay: (id, originUrl) => api.post(`/invoices/${id}/pay?origin_url=${encodeURIComponent(originUrl)}`),
  createCheckoutSession: (id) => api.post(`/invoices/${id}/pay?origin_url=${encodeURIComponent(window.location.origin)}`),
  myInvoices: () => api.get('/invoices/my-invoices'),
  listTags: () => api.get('/invoices/tags/library'),
  createTag: (data) => api.post('/invoices/tags/library', data),
  deleteTag: (id) => api.delete(`/invoices/tags/library/${id}`),
  getPaymentStatus: (sessionId) => api.get(`/invoices/payment-status/${sessionId}`),
  send: (id, type) => api.post(`/invoices/${id}/send`, { type }),
  clientOpen: (clientId) => api.get(`/invoices?client_id=${clientId}&limit=50`),
};

// Tickets API
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  reply: (id, message) => api.post(`/tickets/${id}/reply`, { message }),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  close: (id) => api.post(`/tickets/${id}/close`),
  delete: (id) => api.delete(`/tickets/${id}`),
};

// Domains API
export const domainsAPI = {
  list: (params) => api.get('/domains', { params }),
  get: (id) => api.get(`/domains/${id}`),
  create: (data) => api.post('/domains', data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  delete: (id) => api.delete(`/domains/${id}`),
  searchAvailability: (query) => api.post('/domains/search-availability', { query }),
  createRegistrationCheckout: (data) => api.post('/domains/register/checkout-session', data),
  getRegistrationStatus: (sessionId) => api.get(`/domains/register/status/${sessionId}`),
  adminAssign: (data) => api.post('/domains/admin/assign', data),
  adminRegister: (data) => api.post('/domains/admin/register', data),
  adminRenew: (data) => api.post('/domains/admin/renew', data),
  createRenewalInvoice: (domainId) => api.post(`/domains/${domainId}/client-renewal-invoice`),
  saveBillingMatrix: (domainId, data) => api.put(`/domains/${domainId}/billing-matrix`, data),
  createDirectInvoice: (domainId, data) => api.post(`/domains/${domainId}/create-invoice`, data),
};

export const phoneNumbersAPI = {
  list: (params) => api.get('/phone-numbers', { params }),
  get: (id) => api.get(`/phone-numbers/${id}`),
  create: (data) => api.post('/phone-numbers', data),
  update: (id, data) => api.put(`/phone-numbers/${id}`, data),
  delete: (id) => api.delete(`/phone-numbers/${id}`),
  saveBillingMatrix: (id, data) => api.put(`/phone-numbers/${id}/billing-matrix`, data),
  createDirectInvoice: (id, data) => api.post(`/phone-numbers/${id}/create-invoice`, data),
  getUsage: (id, params) => api.get(`/phone-numbers/${id}/usage`, { params }),
  getMonthlyUsage: (id) => api.get(`/phone-numbers/${id}/usage-monthly`),
  getGlobalMonthlyStats: (year) => api.get('/phone-numbers/usage-stats/monthly', { params: year ? { year } : {} }),
  // Echo system
  getEchoSettings: () => api.get('/phone-numbers/echo/settings'),
  saveEchoSettings: (data) => api.put('/phone-numbers/echo/settings', data),
  testEchoConnection: () => api.get('/phone-numbers/echo/test'),
  pullEchoNumbers: () => api.get('/phone-numbers/echo/numbers'),
  syncFromEcho: () => api.post('/phone-numbers/echo/sync'),
  searchEchoClients: (search) => api.get(`/phone-numbers/echo/clients?search=${search || ''}`),
  pushClientToEcho: (clientId) => api.post(`/phone-numbers/echo/push-client/${clientId}`),
  // Rules & Balance Settings
  getSettings: (id) => api.get(`/phone-numbers/${id}/settings`),
  saveAutoSuspendRule: (id, data) => api.put(`/phone-numbers/${id}/auto-suspend-rule`, data),
  saveClientBalanceSettings: (id, data) => api.put(`/phone-numbers/${id}/client-balance-settings`, data),
};

// Projects API
export const projectsAPI = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  // Costs
  listCosts: (id) => api.get(`/projects/${id}/costs`),
  addCost: (id, data) => api.post(`/projects/${id}/costs`, data),
  updateCost: (id, costId, data) => api.put(`/projects/${id}/costs/${costId}`, data),
  deleteCost: (id, costId) => api.delete(`/projects/${id}/costs/${costId}`),
  // Logins (AES-256 encrypted)
  listLogins: (id) => api.get(`/projects/${id}/logins`),
  addLogin: (id, data) => api.post(`/projects/${id}/logins`, data),
  updateLogin: (id, loginId, data) => api.put(`/projects/${id}/logins/${loginId}`, data),
  deleteLogin: (id, loginId) => api.delete(`/projects/${id}/logins/${loginId}`),
  // SEO
  runSeoAnalysis: (id) => api.post(`/projects/${id}/seo-analyze`),
  getSeoDashboard: (id) => api.get(`/projects/${id}/seo-dashboard`),
  getMySeoDashboard: (id) => api.get(`/my/projects/${id}/seo-dashboard`),
};

// Tasks API
export const tasksAPI = {
  list: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// CRM API
export const crmAPI = {
  list: (params) => api.get('/crm/contacts', { params }),
  listContacts: (params) => api.get('/crm/contacts', { params }),
  get: (id) => api.get(`/crm/contacts/${id}`),
  getContact: (id) => api.get(`/crm/contacts/${id}`),
  create: (data) => api.post('/crm/contacts', data),
  createContact: (data) => api.post('/crm/contacts', data),
  update: (id, data) => api.put(`/crm/contacts/${id}`, data),
  updateContact: (id, data) => api.put(`/crm/contacts/${id}`, data),
  updateStage: (id, stage) => api.put(`/crm/contacts/${id}/stage`, { stage }),
  delete: (id) => api.delete(`/crm/contacts/${id}`),
  deleteContact: (id) => api.delete(`/crm/contacts/${id}`),
  getStats: () => api.get('/crm/stats'),
  getStages: () => api.get('/crm/pipeline-stages'),
  // Tasks
  getTasks: (contactId) => api.get(`/crm/contacts/${contactId}/tasks`),
  createTask: (contactId, data) => api.post(`/crm/contacts/${contactId}/tasks`, data),
  updateTask: (contactId, taskId, data) => api.put(`/crm/contacts/${contactId}/tasks/${taskId}`, data),
  deleteTask: (contactId, taskId) => api.delete(`/crm/contacts/${contactId}/tasks/${taskId}`),
  // Appointments
  getAppointments: (contactId) => api.get(`/crm/contacts/${contactId}/appointments`),
  createAppointment: (contactId, data) => api.post(`/crm/contacts/${contactId}/appointments`, data),
  deleteAppointment: (contactId, apptId) => api.delete(`/crm/contacts/${contactId}/appointments/${apptId}`),
  // Payments
  getPayments: (contactId) => api.get(`/crm/contacts/${contactId}/payments`),
  createPayment: (contactId, data) => api.post(`/crm/contacts/${contactId}/payments`, data),
  deletePayment: (contactId, paymentId) => api.delete(`/crm/contacts/${contactId}/payments/${paymentId}`),
  // Associations
  getAssociations: (contactId) => api.get(`/crm/contacts/${contactId}/associations`),
  createAssociation: (contactId, data) => api.post(`/crm/contacts/${contactId}/associations`, data),
  deleteAssociation: (contactId, assocId) => api.delete(`/crm/contacts/${contactId}/associations/${assocId}`),
};

export const leadsAPI = {
  get: (id) => api.get(`/leads/${id}`),
};

export const billingAPI = {
  getSettings: () => api.get('/billing/settings'),
  updateSettings: (data) => api.put('/billing/settings', data),
  listProducts: () => api.get('/billing/products'),
  listServices: () => api.get('/billing/services'),
};

export const contractTemplatesAPI = {
  list: () => api.get('/contract-templates'),
  create: (data) => api.post('/contract-templates', data),
  update: (id, data) => api.put(`/contract-templates/${id}`, data),
  delete: (id) => api.delete(`/contract-templates/${id}`),
};

export const quotesAPI = {
  listLead: (leadId) => api.get(`/leads/${leadId}/quotes`),
  createLead: (leadId, data) => api.post(`/leads/${leadId}/quotes`, data),
  updateLead: (leadId, quoteId, data) => api.put(`/leads/${leadId}/quotes/${quoteId}`, data),
  deleteLead: (leadId, quoteId) => api.delete(`/leads/${leadId}/quotes/${quoteId}`),
  unlock: (leadId, quoteId, data) => api.post(`/leads/${leadId}/quotes/${quoteId}/unlock`, data),
  sendEmail: (leadId, quoteId, data) => api.post(`/leads/${leadId}/quotes/${quoteId}/send-email`, data),
  sendTest: (leadId, quoteId) => api.post(`/leads/${leadId}/quotes/${quoteId}/send-test`),
  convertToInvoice: (leadId, quoteId) => api.post(`/leads/${leadId}/quotes/${quoteId}/convert-to-invoice`),
  share: (leadId, quoteId, data) => api.post(`/leads/${leadId}/quotes/${quoteId}/share`, data),
  getTracking: (quoteId) => api.get(`/email-tracking/${quoteId}`),
  listClient: (clientId) => api.get(`/clients/${clientId}/quotes`),
  getClientLead: (clientId) => api.post(`/clients/${clientId}/quote-lead`),
  listMyQuotes: () => api.get('/client/my-quotes'),
};

export const usersAPI = {
  list: () => api.get('/users'),
  getProfile: () => api.get('/users/me/profile'),
  updateProfile: (data) => api.put('/users/me/profile', data),
  adminSetPassword: (userId, newPassword) => api.post(`/users/${userId}/set-password`, { new_password: newPassword }),
  adminSendReset: (userId, originUrl) => api.post(`/users/${userId}/send-password-reset`, { origin_url: originUrl }),
  requestPasswordReset: (email, originUrl) => api.post(`/users/password-reset/request`, { email, origin_url: originUrl }),
  validateResetToken: (token) => api.get(`/users/password-reset/validate`, { params: { token } }),
  completePasswordReset: (token, newPassword) => api.post(`/users/password-reset/complete`, { token, new_password: newPassword }),
};

export const categoriesAPI = {
  list: (entity) => api.get('/categories', { params: { entity } }),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  remove: (id) => api.delete(`/categories/${id}`),
};

export const spaceshipAPI = {
  status: () => api.get('/spaceship/status'),
  getEffectivePricing: () => api.get('/spaceship/pricing/effective'),
  listClientDomainPricing: () => api.get('/spaceship/clients/domain-pricing'),
  testConnection: () => api.post('/spaceship/test-connection'),
  listDomains: (params) => api.get('/spaceship/domains', { params }),
  syncDomains: () => api.post('/spaceship/domains/sync'),
  checkAvailability: (domains) => api.post('/spaceship/domains/check-availability', { domains }),
  getDomain: (domain) => api.get(`/spaceship/domains/${domain}`),
  getDnsRecords: (domain, params) => api.get(`/spaceship/domains/${domain}/dns-records`, { params }),
  updateNameservers: (domain, data) => api.put(`/spaceship/domains/${domain}/nameservers`, data),
  getAuthCode: (domain) => api.get(`/spaceship/domains/${domain}/auth-code`),
  renewDomain: (domain) => api.post(`/spaceship/domains/${domain}/renew`),
  getAsyncOperation: (operationId) => api.get(`/spaceship/async/${operationId}`),
  // TLD Pricing
  getTldPrices: () => api.get('/spaceship/tld-prices'),
  syncTldPrices: () => api.post('/spaceship/tld-prices/sync'),
  updateTldPrice: (tld, cost, renewCost) => api.put(`/spaceship/tld-prices/${tld}`, { cost, renew_cost: renewCost }),
  // Default registrant contact
  getDefaultContact: () => api.get('/spaceship/contact/default'),
  createDefaultContact: () => api.post('/spaceship/contact/create'),
  resetDefaultContact: () => api.delete('/spaceship/contact/reset'),
};

export const publicDomainsAPI = {
  search: (query) => api.post('/public/domains/search', { query }),
  createCheckoutSession: (data) => api.post('/public/domains/checkout/session', data),
  getCheckoutStatus: (sessionId) => api.get(`/public/domains/checkout/status/${sessionId}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getActivity: (limit = 10) => api.get('/activity', { params: { limit } }),
  getBillingStats: () => api.get('/dashboard/billing-stats'),
};

// Forms API (Superuser only)
export const formsAPI = {
  list: () => api.get('/forms'),
  get: (id) => api.get(`/forms/${id}`),
  create: (data) => api.post('/forms', data),
  update: (id, data) => api.put(`/forms/${id}`, data),
  delete: (id) => api.delete(`/forms/${id}`),
  getSubmissions: (id, params) => api.get(`/forms/${id}/submissions`, { params }),
};

// Settings API
export const settingsAPI = {
  getGeneral: () => api.get('/settings/general'),
  saveGeneral: (settings) => api.post('/settings/general', { settings }),
};

// Registrars API
export const registrarsAPI = {
  getSettings: () => api.get('/registrars/settings'),
  getRawSettings: () => api.get('/registrars/settings/raw'),
  saveSettings: (registrar, settings) => api.post('/registrars/settings', { registrar, settings }),
  testConnection: (registrar) => api.post(`/registrars/${registrar}/test-connection`),
  searchDomains: (registrar, query) => api.post(`/registrars/${registrar}/search`, { query }),
  listDomains: (registrar) => api.get(`/registrars/${registrar}/domains`),
  whoisLookup: (registrar, domain) => api.get(`/registrars/${registrar}/whois/${domain}`),
  syncDomains: () => api.post('/registrars/sync'),
};

// System API
export const systemAPI = {
  getInfo: () => api.get('/system/info'),
};

// Client Services API
export const clientServicesAPI = {
  list: (clientId) => api.get(`/clients/${clientId}/services`),
  add: (clientId, data) => api.post(`/clients/${clientId}/services`, data),
  update: (clientId, serviceId, data) => api.put(`/clients/${clientId}/services/${serviceId}`, data),
  remove: (clientId, serviceId) => api.delete(`/clients/${clientId}/services/${serviceId}`),
  listPayments: (clientId) => api.get(`/clients/${clientId}/payments`),
  recordPayment: (clientId, data) => api.post(`/clients/${clientId}/payments`, data),
  chargeStripe: (clientId, serviceId, originUrl) =>
    api.post(`/clients/${clientId}/services/${serviceId}/stripe-charge?origin_url=${encodeURIComponent(originUrl)}`),
  stripePaymentStatus: (clientId, sessionId) =>
    api.get(`/clients/${clientId}/stripe-payment-status/${sessionId}`),
};

// Payment Gateways API
export const paymentGatewaysAPI = {
  getSettings: () => api.get('/payment-gateways/settings'),
  getEnabled: () => api.get('/payment-gateways/enabled'),
  getRawSettings: () => api.get('/payment-gateways/settings/raw'),
  saveSettings: (gateway, settings) => api.post('/payment-gateways/settings', { gateway, settings }),
  testStripe: () => api.post('/payment-gateways/stripe/test-connection'),
};

// Email Templates API
export const emailTemplatesAPI = {
  list: () => api.get('/email-templates'),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  preview: (id, data) => api.post(`/email-templates/${id}/preview`, data),
  mergeFields: () => api.get('/email-templates/merge-fields/reference'),
};

// Servers API (cPanel/WHM Management)
export const serversAPI = {
  // Server management
  list: () => api.get('/servers'),
  get: (id) => api.get(`/servers/${id}`),
  create: (data) => api.post('/servers', data),
  update: (id, data) => api.put(`/servers/${id}`, data),
  delete: (id) => api.delete(`/servers/${id}`),
  test: (id) => api.post(`/servers/${id}/test`),
  authDiagnostics: (id) => api.post(`/servers/${id}/auth-diagnostics`),
  getStats: (id) => api.get(`/servers/${id}/stats`),
  sync: (id) => api.post(`/servers/${id}/sync`),
  
  // Account management
  listAccounts: (serverId, search) => api.get(`/servers/${serverId}/accounts`, { params: { search } }),
  getAccount: (serverId, username) => api.get(`/servers/${serverId}/accounts/${username}`),
  createAccount: (serverId, data) => api.post(`/servers/${serverId}/accounts`, data),
  accountAction: (serverId, username, action, reason) => 
    api.post(`/servers/${serverId}/accounts/${username}/action`, { action, reason }),
  changePassword: (serverId, username, newPassword) =>
    api.post(`/servers/${serverId}/accounts/${username}/password`, null, { params: { new_password: newPassword } }),
  
  // Package management
  listPackages: (serverId) => api.get(`/servers/${serverId}/packages`),
};

// Inbox API (Contact Form Submissions)
export const inboxAPI = {
  list: () => api.get('/contact/inbox'),
  get: (id) => api.get(`/contact/inbox/${id}`),
  markRead: (id) => api.put(`/contact/inbox/${id}/read`),
  route: (id, data) => api.post(`/contact/inbox/${id}/route`, data),
  delete: (id) => api.delete(`/contact/inbox/${id}`),
  reply: (id, data) => api.post(`/contact/inbox/${id}/reply`, data),
};

// Payment Methods API
export const paymentMethodsAPI = {
  list: (clientId) => api.get(`/clients/${clientId}/payment-methods`),
  setup: (clientId, originUrl) =>
    api.post(`/clients/${clientId}/payment-methods/setup?origin_url=${encodeURIComponent(originUrl)}`),
  remove: (clientId, pmId) => api.delete(`/clients/${clientId}/payment-methods/${pmId}`),
  saveNickname: (clientId, pmId, nickname) =>
    api.patch(`/clients/${clientId}/payment-methods/${pmId}/nickname`, { nickname }),
};

// Businesses API
export const businessesAPI = {
  list: (clientId) => api.get(`/clients/${clientId}/businesses`),
  get: (clientId, businessId) => api.get(`/clients/${clientId}/businesses/${businessId}`),
  create: (clientId, data) => api.post(`/clients/${clientId}/businesses`, data),
  update: (clientId, businessId, data) => api.put(`/clients/${clientId}/businesses/${businessId}`, data),
  delete: (clientId, businessId) => api.delete(`/clients/${clientId}/businesses/${businessId}`),
  listServices: (clientId, businessId) => api.get(`/clients/${clientId}/businesses/${businessId}/services`),
  listInvoices: (clientId, businessId) => api.get(`/clients/${clientId}/businesses/${businessId}/invoices`),
  listPayments: (clientId, businessId) => api.get(`/clients/${clientId}/businesses/${businessId}/payments`),
  addService: (clientId, businessId, data) => api.post(`/clients/${clientId}/businesses/${businessId}/services`, data),
  syncStripe: (clientId, businessId) => api.post(`/clients/${clientId}/businesses/${businessId}/sync-stripe`),
  // Client-portal (authenticated as self)
  myList: () => api.get('/clients/my-businesses'),
  myGet: (businessId) => api.get(`/clients/my-businesses/${businessId}`),
  myInvoices: (businessId) => api.get(`/clients/my-businesses/${businessId}/invoices`),
};

export default api;

// Client Groups API
export const clientGroupsAPI = {
  list: () => api.get('/client-groups'),
  get: (id) => api.get(`/client-groups/${id}`),
  create: (data) => api.post('/client-groups', data),
  update: (id, data) => api.put(`/client-groups/${id}`, data),
  delete: (id) => api.delete(`/client-groups/${id}`),
};

// Storage API
export const storageAPI = {
  getSettings: () => api.get('/storage/settings'),
  saveSettings: (data) => api.post('/storage/settings', data),
  testConnection: () => api.post('/storage/test-connection'),
  browse: (prefix) => api.get('/storage/browse', { params: { prefix } }),
  createFolder: (data) => api.post('/storage/create-folder', data),
  upload: (file, prefix) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('prefix', prefix || '');
    return api.post('/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getDownloadUrl: (key, clientId) => api.get('/storage/download-url', { params: { key, client_id: clientId || undefined } }),
  createShortLink: (key, clientId) => api.post('/storage/short-link', { key, client_id: clientId || null }),
  downloadLog: (params) => api.get('/storage/download-log', { params }),
  deleteObject: (key) => api.delete('/storage/delete', { params: { key } }),
  rename: (data) => api.post('/storage/rename', data),
  initClientFolders: () => api.post('/storage/init-client-folders'),
  // Client-scoped
  clientFiles: (clientId, subfolder) => api.get(`/storage/client/${clientId}/files`, { params: { subfolder: subfolder || '' } }),
  clientUpload: (clientId, file, subfolder) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('subfolder', subfolder || '');
    return api.post(`/storage/client/${clientId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  clientCreateFolder: (clientId, data) => api.post(`/storage/client/${clientId}/create-folder`, data),
  clientDelete: (clientId, key) => api.delete(`/storage/client/${clientId}/delete`, { params: { key } }),
  // Client portal
  myFiles: (subfolder) => api.get('/storage/my-files', { params: { subfolder: subfolder || '' } }),
  // Site Backups
  listSiteBackups: (clientId) => api.get(`/storage/client/${clientId}/site-backups`),
  uploadSiteBackup: (clientId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/storage/client/${clientId}/site-backups/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  startChunkedUpload: (clientId, filename) => api.post(`/storage/client/${clientId}/site-backups/start-upload`, { filename }),
  uploadChunk: (clientId, formData) => api.post(`/storage/client/${clientId}/site-backups/upload-chunk`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  completeChunkedUpload: (clientId, data) => api.post(`/storage/client/${clientId}/site-backups/complete-upload`, data),
  abortChunkedUpload: (clientId, data) => api.post(`/storage/client/${clientId}/site-backups/abort-upload`, data),
  deleteSiteBackup: (clientId, key) => api.delete(`/storage/client/${clientId}/site-backups`, { params: { key } }),
  getRetentionPolicy: (clientId) => api.get(`/storage/client/${clientId}/site-backups/retention`),
  setRetentionPolicy: (clientId, data) => api.put(`/storage/client/${clientId}/site-backups/retention`, data),
  mySiteBackups: () => api.get('/storage/my-site-backups'),
};

export const backupsAPI = {
  getSettings: () => api.get('/backups/settings'),
  saveSettings: (data) => api.post('/backups/settings', data),
  runNow: () => api.post('/backups/run'),
  runAndEmail: () => api.post('/backups/run-and-email'),
  emailBackup: (id) => api.post(`/backups/${id}/email`),
  history: () => api.get('/backups/history'),
  download: (id) => api.get(`/backups/download/${id}`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/backups/${id}`),
  restoreExisting: (id, confirm) => api.post(`/backups/restore/${id}`, { confirm }),
  restoreStart: (filename) => {
    const fd = new FormData();
    fd.append('filename', filename);
    return api.post('/backups/restore/start', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  restoreChunk: (fd) => api.post('/backups/restore/chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  restoreComplete: (data) => api.post('/backups/restore/complete', data, { timeout: 600000 }),
};

export const gatewayLogAPI = {
  list: (params) => api.get('/gateway-log', { params }),
  get: (id) => api.get(`/gateway-log/${id}`),
  listNotifications: (params) => api.get('/gateway-log/notifications/list', { params }),
};

export const automationAPI = {
  status: () => api.get('/automation/status'),
  dailyActions: (date) => api.get('/automation/daily-actions', { params: { date } }),
  chart: (metric, period) => api.get('/automation/chart', { params: { metric, period } }),
  systemTasks: () => api.get('/automation/system-tasks'),
  runCron: () => api.post('/automation/run-cron'),
};


export const transactionsAPI = {
  list: (params) => api.get('/transactions', { params }),
  chart: (period) => api.get('/transactions/chart', { params: { period } }),
  gatewayBalances: () => api.get('/transactions/gateway-balances'),
  create: (data) => api.post('/transactions', data),
  delete: (id) => api.delete(`/transactions/${id}`),
};



// Client Billing API
export const clientBillingAPI = {
  getStripeConfig: () => api.get('/client-billing/stripe-config'),
  createSetupIntent: (data) => api.post('/client-billing/setup-intent', data),
  listPaymentMethods: () => api.get('/client-billing/payment-methods'),
  setDefault: (paymentMethodId) => api.post('/client-billing/set-default', { payment_method_id: paymentMethodId }),
  deletePaymentMethod: (id) => api.delete(`/client-billing/payment-methods/${id}`),
  // PayPal
  getPayPalConfig: () => api.get('/client-billing/paypal-config'),
  createPayPalOrder: (data) => api.post('/client-billing/paypal/create-order', data),
  capturePayPalOrder: (data) => api.post('/client-billing/paypal/capture-order', data),
  // Admin: Stripe sync
  adminGetStripeInfo: (clientId) => api.get(`/client-billing/admin/stripe-info/${clientId}`),
  adminSyncStripe: (clientId, data) => api.post(`/client-billing/admin/sync-stripe/${clientId}`, data),
  adminCharge: (clientId, data) => api.post(`/client-billing/admin/charge/${clientId}`, data),
};

// Card Assignments API
export const cardAssignmentsAPI = {
  // Admin
  list: (clientId) => api.get(`/clients/${clientId}/card-assignments`),
  assign: (clientId, data) => api.put(`/clients/${clientId}/card-assignments`, data),
  // Client portal (uses /api/clients/my-card-assignments)
  myList: () => api.get('/clients/my-card-assignments'),
  myAssign: (data) => api.put('/clients/my-card-assignments', data),
};

// Barter API
export const barterAPI = {
  list: (params) => api.get('/barter/contracts', { params }),
  get: (id) => api.get(`/barter/contracts/${id}`),
  create: (data) => api.post('/barter/contracts', data),
  update: (id, data) => api.patch(`/barter/contracts/${id}`, data),
  send: (id, data) => api.post(`/barter/contracts/${id}/send`, data),
  stats: () => api.get('/barter/stats'),
};
